import i18nFactory from '@yandex-int/i18n';
import moment from 'moment';
import React from 'react';
import { PushAction } from 'redux-little-router';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { EMPTY, from, Observable, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { Error } from 'tslint/lib/error';

import { Action } from '../../../actions';
import {
    acceptCampaignAndGroupSelectorData,
    acceptCampaignAndGroupSelectorDataDna,
    selectCampaignAndGroupSelectorCampaign,
    selectCampaignAndGroupSelectorGroup,
    setCampaignAndGroupSelectorEntityType
} from '../../../actions/campaignAndGroupSelector';
import { ADD_GROUP_CAMPAIGN_GRID, AddGroupCampaignGridAction } from '../../../actions/campaigns-grid';
import { changeLocation, replaceLocation } from '../../../actions/common';
import {
    ADD_BANNER_GROUP_GRID,
    AddBannerGroupGridAction,
    editCampaign,
    EXPORT_TO_EXCEL,
    exportToExcel,
    ExportToExcelAction,
    GO_TO_BANNERS_EDIT_FOR_CREATION,
    GO_TO_CAMPAIGNS_EDIT_FOR_CREATION,
    GO_TO_GROUPS_EDIT_FOR_CREATION,
    GO_TO_INTERNAL_BANNERS_EDIT_FOR_CREATION,
    goToBannersEdit,
    GoToBannersEditForCreationAction,
    goToBannersMassEdit,
    goToCampaignModerationOffer,
    GoToCampaignsEditForCreationAction,
    goToDirectPaymentInstructions,
    goToGroupsEdit,
    GoToGroupsEditForCreationAction,
    goToInternalBannersEdit,
    GoToInternalBannersEditAction,
    GoToInternalBannersEditForCreationAction,
    goToInternalBannersMassEdit,
    goToLogs,
    goToStatistics,
    GRID_SCREEN_CANCEL_CELL_EDIT,
    GRID_SCREEN_GO_TO_BANNERS_EDIT,
    GRID_SCREEN_GO_TO_BANNERS_MASS_EDIT,
    GRID_SCREEN_GO_TO_CAMPAIGN_MODERATION_OFFER,
    GRID_SCREEN_GO_TO_CAMPAIGNS_EDIT,
    GRID_SCREEN_GO_TO_DIRECT_PAYMENT_INSTRUCTIONS,
    GRID_SCREEN_GO_TO_GROUPS_EDIT,
    GRID_SCREEN_GO_TO_INTERNAL_BANNERS_EDIT,
    GRID_SCREEN_GO_TO_INTERNAL_BANNERS_MASS_EDIT,
    GRID_SCREEN_GO_TO_LOGS,
    GRID_SCREEN_GO_TO_STATISTICS,
    GRID_SCREEN_HANDLE_COLUMNS_ORDER_CHANGE,
    GRID_SCREEN_HANDLE_COLUMNS_VISIBILITY_CHANGE,
    GRID_SCREEN_HANDLE_COLUMNS_WIDTH_UPDATE,
    GRID_SCREEN_HANDLE_ITEM_ACTION,
    GRID_SCREEN_HANDLE_NAVIGATION_LINK_CLICK,
    GridScreenCancelCellEditAction,
    gridScreenChangeColumnsOrder,
    gridScreenChangeColumnsVisibility,
    GridScreenGoToBannersEditAction,
    GridScreenGoToCampaignModerationOfferAction,
    GridScreenGoToCampaignsEditAction,
    GridScreenGoToDirectPaymentInstructions,
    GridScreenGoToGroupsEditAction,
    GridScreenGoToStatisticsAction,
    GridScreenHandleColumnsOrderChangeAction,
    GridScreenHandleColumnsVisibilityChangeAction,
    GridScreenHandleColumnsWidthUpdateAction,
    GridScreenHandleItemAction,
    GridScreenHandleNavigationLinkClickAction,
    gridScreenUpdateColumnsWidth,
    openDuplicateGroupsModal,
    OpenMinusPhraseSelectorGridModal,
    resetGridData,
    ResetGridData,
    UpdateActionProgress,
    updateActionProgress,
    updateEditCellData,
    UpdateGridSelection,
    updateSelection
} from '../../../actions/grid-screen';
import { FormRequestManager } from '../../../api/FormRequestManager';
import { StyledLink, StyledLinkTheme } from '../../../components/StyledLink/StyledLink';
import { mapDateRangePresetToDatePeriod } from '../../../mappers/common/common';
import { selectGridSourceParams } from '../../../selectors/gridSourceParams/gridSourceParams';
import {
    getColumnSettingsWithUpdatedOrder,
    getColumnsVisibilitySettings,
    getColumnsWidthSettings
} from '../../../service/ActionHelpers';
import { alert } from '../../../service/Alert/Alert';
import { BannersIdsForBannersEditHelpers } from '../../../service/BannersIdsForBannersEditHelpers';
import { getAccessRightsForCampaignAndGroupSelector } from '../../../service/CampaignAndGroupSelectorHelpers';
import { checkIsCampaignUpdateAllowedInDna } from '../../../service/CampaignsEditHelpers/CampaignsEditHelpers';
import { CampaignsIdsForCampaignsEditHelpers } from '../../../service/CampaignsIdsForCampaignsEditHelpers';
import { confirm } from '../../../service/Confirm/Confirm';
import { confirmPayAction } from '../../../service/ConfirmPayAction/ConfirmPayAction';
import { ApiMethodExecutingError, NotSyncedError } from '../../../service/Errors';
import { getEditCellData, showNoticeMessage } from '../../../service/GridHelpers/GridHelpers';
import {
    getGridActionErrorMessage,
    getGroupItemActionsResultMessage,
    getTextForItemActionConfirm,
    getTextForRowItemActionConfirm,
    showRetargetingActionNoticeMessage
} from '../../../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import { GroupIdsForGroupsEditHelpers } from '../../../service/GroupIdsForGroupsEditHelpers';
import { getNoticeParamsForMassAction } from '../../../service/NoticeTypeHelper/NoticeTypeHelper';
import { LocalStorage, StorageKeyType } from '../../../service/Storage/Storage';
import { EpicDeps } from '../../../service/StoreFactory';
import { UrlHelpers } from '../../../service/UrlHelpers';
import { BannersScreenUrlSearchQueryParams } from '../../../typings/banners';
import { CampaignsScreenUrlSearchQueryParams } from '../../../typings/campaigns';
import { banners, campaigns, groups, LinkTarget, retargetings } from '../../../typings/common';
import { Group } from '../../../typings/domain';
import { DnaEpic, DnaEpicAction } from '../../../typings/epic';
import { GdCampaignType, RemoderateAdsCallouts } from '../../../typings/graphql-grid';
import {
    BannersAction,
    CampaignsAction,
    GridRouterParams,
    GroupsAction,
    NoticeType,
    PayActionInfo,
    RetargetingsAction
} from '../../../typings/grid';
import { GroupId, GroupsScreenUrlSearchQueryParams } from '../../../typings/groups';
import { State } from '../../../typings/state';
import { XlsExportMode, XlsExportParams } from '../../../typings/xls';

import * as keyset from './grid-screen-helpers.i18n';

const i18n = i18nFactory(keyset);

const gridScreenCancelCellEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GRID_SCREEN_CANCEL_CELL_EDIT),
        mergeMap((action: GridScreenCancelCellEditAction) => {
            const state = store$.value;
            const sourceParams = selectGridSourceParams(state);
            const { editCellsData } = store$.value.grid;

            const cellKey = {
                id: action.id,
                column: action.columnName,
                view: sourceParams.view
            };

            const { isSaving } = getEditCellData(editCellsData, action.id, action.columnName, sourceParams.view);

            if (!isSaving) {
                return of(
                    updateEditCellData(cellKey, {
                        isSaving: false,
                        error: ''
                    })
                );
            }
            return EMPTY;
        })
    );
};

const gridScreenGoToStatisticsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GRID_SCREEN_GO_TO_STATISTICS),
        mergeMap((action: GridScreenGoToStatisticsAction) => {
            const state = store$.value;
            const sourceParams = selectGridSourceParams(state);
            const { selectionInfo } = state.grid;
            let selectedInfo = action.campaignIds || [];
            let statPeriod;

            if (selectedInfo.length === 0) {
                selectedInfo = selectionInfo[sourceParams.view];
            }

            if (!sourceParams.statPeriod) {
                return EMPTY;
            }

            const selectedStatPeriod = sourceParams.statPeriod;

            if (selectedStatPeriod.statPreset) {
                const statDates = mapDateRangePresetToDatePeriod(moment(), selectedStatPeriod.statPreset);

                statPeriod = {
                    dateFrom: statDates.dateFrom.format('YYYY-MM-DD'),
                    dateTo: statDates.dateTo.format('YYYY-MM-DD')
                };
            } else if (selectedStatPeriod.statFrom && selectedStatPeriod.statTo) {
                statPeriod = {
                    dateFrom: selectedStatPeriod.statFrom,
                    dateTo: selectedStatPeriod.statTo
                };
            } else {
                return EMPTY;
            }

            const getSubmitParamsHelper = {
                clients:
                    selectedInfo.length === 1
                        ? UrlHelpers.getCampaignMocRequestParams
                        : UrlHelpers.getCampaignsMolRequestParams,
                campaigns: UrlHelpers.getGroupsMolRequestParams
            }[sourceParams.context];

            if (getSubmitParamsHelper) {
                FormRequestManager.submit(
                    getSubmitParamsHelper(selectedInfo, sourceParams.login, statPeriod, action.target)
                );
            } else {
                throw new Error(`Для контекста ${sourceParams.context} не поддерживается переход на статистику`);
            }
            return EMPTY;
        })
    );
};

const gridScreenGoToCampaignModerationOfferEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GRID_SCREEN_GO_TO_CAMPAIGN_MODERATION_OFFER),
        mergeMap((action: GridScreenGoToCampaignModerationOfferAction) => {
            const state = store$.value;
            const { selectionInfo } = state.grid;
            const sourceParams = selectGridSourceParams(state);
            const selectedCampaigns = selectionInfo[campaigns];
            const campaign = action.campaignId || selectedCampaigns[0];

            FormRequestManager.submit(
                UrlHelpers.getCampaignModerationOfferParams(campaign, sourceParams.login, action.target)
            );
            return EMPTY;
        })
    );
};

const gridScreenGoToDirectPaymentInstructions: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(GRID_SCREEN_GO_TO_DIRECT_PAYMENT_INSTRUCTIONS).mergeMap(
        (action: GridScreenGoToDirectPaymentInstructions): Observable<Action> => {
            const state = store$.value;
            const sourceParams = selectGridSourceParams(state);
            const {
                selectionInfo: { campaigns: selectedCampaigns }
            } = store$.value.grid;

            const payObservable$ = Observable.fromPromise(
                deps.entitiesDataProvider.fetchPayActionInfo(sourceParams.login, selectedCampaigns)
            )
                .mergeMap(
                    ({ campaignTypes, unsupportedCampaigns }: PayActionInfo): Observable<UpdateActionProgress> => {
                        const clickCampaignsSelected = campaignTypes.some(
                            el => ![GdCampaignType.CPM_DEALS, GdCampaignType.CPM_BANNER].includes(el)
                        );
                        const cpmDealsCampaginSelected = campaignTypes.includes(GdCampaignType.CPM_DEALS);
                        const cpmBannerCampaginsSelected = campaignTypes.includes(GdCampaignType.CPM_BANNER);

                        if (cpmDealsCampaginSelected && (clickCampaignsSelected || cpmBannerCampaginsSelected)) {
                            showNoticeMessage(NoticeType.error, i18n('pay-not-allowed-deals'));

                            return Observable.of(updateActionProgress(false));
                        }

                        if (cpmBannerCampaginsSelected && clickCampaignsSelected) {
                            showNoticeMessage(NoticeType.error, i18n('pay-not-allowed-cpm'));
                            return Observable.of(updateActionProgress(false));
                        }

                        const confirmationPromise = Promise.resolve().then(() => {
                            if (unsupportedCampaigns.length === 0) {
                                return undefined;
                            }

                            return confirmPayAction(unsupportedCampaigns);
                        });

                        const supportedCampaignIds = selectedCampaigns.filter(
                            (el: string) => !unsupportedCampaigns.some(campaign => campaign.id === el)
                        );

                        return Observable.concat(
                            Observable.of(updateActionProgress(false)),
                            Observable.fromPromise(confirmationPromise).mergeMap(() => {
                                FormRequestManager.submit(
                                    UrlHelpers.getCampaignPaymentInstructionsPage(
                                        supportedCampaignIds,
                                        sourceParams.login
                                    )
                                );

                                return Observable.of(updateActionProgress(false));
                            })
                        );
                    }
                )
                .catch(() => Observable.of(updateActionProgress(false)));

            return Observable.concat(Observable.of(updateActionProgress(true)), payObservable$);
        }
    );
};

const gridScreenGoToBannersMassEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GRID_SCREEN_GO_TO_BANNERS_MASS_EDIT),
        mergeMap(
            (): Observable<GridScreenGoToBannersEditAction> => {
                const state = store$.value;
                const { selectionInfo } = state.grid;
                const sourceParams = selectGridSourceParams(state);

                if (sourceParams && sourceParams.login) {
                    return Observable.of(
                        goToBannersEdit(sourceParams.login, selectionInfo[campaigns], selectionInfo[banners])
                    );
                } else {
                    throw new Error(`Не указан логин клиента`);
                }
            }
        )
    );
};

const gridScreenGoToBannersEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.ofType(GRID_SCREEN_GO_TO_BANNERS_EDIT).mergeMap(
        ({ login, campaignsIds, bannersIds }: GridScreenGoToBannersEditAction): Observable<PushAction> => {
            if (!campaignsIds.length || !bannersIds.length) {
                return EMPTY;
            }

            if (login) {
                BannersIdsForBannersEditHelpers.setCampAndBannerIds(login, bannersIds);

                return Observable.concat(
                    Observable.of(
                        changeLocation(
                            UrlHelpers.getBannersEditUrl({
                                [BannersScreenUrlSearchQueryParams.ulogin]: login,
                                [BannersScreenUrlSearchQueryParams.campaignsIds]: campaignsIds,
                                [BannersScreenUrlSearchQueryParams.bannersIds]: bannersIds
                            }),
                            undefined,
                            false
                        )
                    )
                );
            } else {
                throw new Error(`Не указан логин клиента`);
            }
        }
    );
};

const goToInternalBannersEditForCreationEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GO_TO_INTERNAL_BANNERS_EDIT_FOR_CREATION),
        mergeMap(
            (action: GoToInternalBannersEditForCreationAction): Observable<PushAction> => {
                const { login, campaignId, groupId } = action;
                const groupsIds: Array<GroupId> = [groupId];
                const campaignsIds = [campaignId];

                BannersIdsForBannersEditHelpers.setCampAndGroupIdsForCreation(login, campaignsIds, groupsIds);

                return of(
                    changeLocation(
                        UrlHelpers.getInternalBannersEditUrl({
                            [BannersScreenUrlSearchQueryParams.ulogin]: login,
                            [BannersScreenUrlSearchQueryParams.campaignsIds]: campaignsIds,
                            [BannersScreenUrlSearchQueryParams.groupsIds]: groupsIds,
                            [BannersScreenUrlSearchQueryParams.isNew]: '1'
                        }),
                        undefined,
                        false
                    )
                );
            }
        )
    );
};

const goToInternalBannersEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GRID_SCREEN_GO_TO_INTERNAL_BANNERS_EDIT),
        mergeMap(
            ({ login, campaignsIds, bannersIds }: GoToInternalBannersEditAction): Observable<PushAction> => {
                if (!campaignsIds.length || !bannersIds.length) {
                    return EMPTY;
                }

                if (login) {
                    BannersIdsForBannersEditHelpers.setCampAndBannerIds(login, bannersIds);

                    return of(
                        changeLocation(
                            UrlHelpers.getInternalBannersEditUrl({
                                [BannersScreenUrlSearchQueryParams.ulogin]: login,
                                [BannersScreenUrlSearchQueryParams.campaignsIds]: campaignsIds,
                                [BannersScreenUrlSearchQueryParams.bannersIds]: bannersIds
                            }),
                            undefined,
                            false
                        )
                    );
                } else {
                    throw new Error(`Не указан логин клиента`);
                }
            }
        )
    );
};

const goToInternalBannersMassEditEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$
): Observable<GoToInternalBannersEditAction> => {
    return action$.pipe(
        ofType(GRID_SCREEN_GO_TO_INTERNAL_BANNERS_MASS_EDIT),
        mergeMap(() => {
            const state = store$.value;
            const { selectionInfo } = state.grid;
            const sourceParams = selectGridSourceParams(state);

            if (!sourceParams) {
                return EMPTY;
            }

            if (sourceParams && sourceParams.login) {
                return of(
                    goToInternalBannersEdit(sourceParams.login, selectionInfo[campaigns], selectionInfo[banners])
                );
            } else {
                throw new Error(`Не указан логин клиента`);
            }
        })
    );
};

const gridScreenGoToCampaingsEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GRID_SCREEN_GO_TO_CAMPAIGNS_EDIT),
        switchMap((action: GridScreenGoToCampaignsEditAction): Action[] => {
            const { login, campaignsIds, campaignsTypes } = action;
            const state = store$.value;
            const {
                commonData: { operatorFeatures, features: clientFeatures }
            } = state;

            if (campaignsIds.length > 1) {
                throw new Error('Массовое редактирование кампаний пока недоступно');
            }

            const isCampaignUpdateAllowed = checkIsCampaignUpdateAllowedInDna(
                campaignsTypes[0],
                operatorFeatures,
                clientFeatures
            );

            if (!login) {
                throw new Error(`Не указан логин клиента`);
            }

            if (!isCampaignUpdateAllowed) {
                return [
                    editCampaign(
                        campaignsIds[0], // на редактирование в старом интерфейсе можно открывать 1 кампанию
                        login
                    )
                ];
            }

            CampaignsIdsForCampaignsEditHelpers.setCampaignsIds(login, campaignsIds);

            return [
                changeLocation(
                    UrlHelpers.getCampaignsEditUrl({
                        [CampaignsScreenUrlSearchQueryParams.campaignsIds]: campaignsIds,
                        [CampaignsScreenUrlSearchQueryParams.ulogin]: login
                    }),
                    undefined,
                    false
                )
            ];
        })
    );
};

const gridScreenGoToGroupsEditEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.ofType(GRID_SCREEN_GO_TO_GROUPS_EDIT).mergeMap(
        (action: GridScreenGoToGroupsEditAction): Observable<Action> => {
            const { login, campaignsIds, groupsIds, isNewEmptyGroup } = action;

            GroupIdsForGroupsEditHelpers.setCampAndGroupIds(login, groupsIds, campaignsIds);
            const urlQueryBaseParams = {
                [GroupsScreenUrlSearchQueryParams.campaignsIds]: campaignsIds,
                [GroupsScreenUrlSearchQueryParams.groupsIds]: groupsIds,
                [GroupsScreenUrlSearchQueryParams.ulogin]: login
            };
            let urlQueryParams = urlQueryBaseParams;

            if (action.replaceLocation) {
                urlQueryParams = {
                    ...urlQueryParams,
                    ...{ forceGroupsEditNewVisit: '1' }
                };
            }

            if (isNewEmptyGroup) {
                urlQueryParams = {
                    ...urlQueryParams,
                    ...{
                        [GroupsScreenUrlSearchQueryParams.isNewEmptyGroup]: '1'
                    }
                };
            }

            const url = UrlHelpers.getGroupsEditUrl(urlQueryParams);

            return from([
                action.replaceLocation ? replaceLocation(url, undefined, false) : changeLocation(url, undefined, false),
                resetGridData()
            ]);
        }
    );
};

const goToGroupsEditForCreationEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GO_TO_GROUPS_EDIT_FOR_CREATION),
        mergeMap(
            (action: GoToGroupsEditForCreationAction): Observable<PushAction | ResetGridData> => {
                const { login, campaignId } = action;
                const groupsIds: Array<GroupId> = [];
                const campaignsIds = [campaignId];

                GroupIdsForGroupsEditHelpers.setCampAndGroupIds(login, groupsIds, campaignsIds, true);

                return Observable.of(
                    changeLocation(
                        UrlHelpers.getGroupsEditUrl({
                            [GroupsScreenUrlSearchQueryParams.campaignsIds]: campaignsIds,
                            [GroupsScreenUrlSearchQueryParams.groupsIds]: groupsIds,
                            [GroupsScreenUrlSearchQueryParams.ulogin]: login,
                            [GroupsScreenUrlSearchQueryParams.isNew]: '1',
                            [GroupsScreenUrlSearchQueryParams.isNewEmptyGroup]: '1'
                        }),
                        undefined,
                        false
                    )
                );
            }
        )
    );
};

const goToGroupsEditForCreationFromGridEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofType(ADD_GROUP_CAMPAIGN_GRID),
        mergeMap(
            (action: AddGroupCampaignGridAction): DnaEpicAction => {
                const { entityToSelectType, campaign } = action;
                const {
                    grid: {
                        campaignAndGroupSelector: { groupSelected }
                    },
                    commonData: { operatorFeatures, accessRights, features: clientFeatures }
                } = store$.value;
                const { canAddInDna } = getAccessRightsForCampaignAndGroupSelector({
                    clientFeatures,
                    operatorFeatures,
                    accessRights,
                    type: entityToSelectType,
                    campaign,
                    group: groupSelected
                });

                return from([
                    setCampaignAndGroupSelectorEntityType(entityToSelectType),
                    selectCampaignAndGroupSelectorCampaign(campaign),
                    canAddInDna ? acceptCampaignAndGroupSelectorDataDna() : acceptCampaignAndGroupSelectorData()
                ]);
            }
        )
    );
};

const goToBannerEditForCreationFromGridEpic: DnaEpic = (action$, store$) => {
    return action$.pipe(
        ofType(ADD_BANNER_GROUP_GRID),
        mergeMap(
            (action: AddBannerGroupGridAction): DnaEpicAction => {
                const { entityToSelectType, campaign, group } = action;
                const {
                    commonData: { operatorFeatures, accessRights, features: clientFeatures }
                } = store$.value;
                const { canAddInDna } = getAccessRightsForCampaignAndGroupSelector({
                    clientFeatures,
                    operatorFeatures,
                    accessRights,
                    type: entityToSelectType,
                    campaign,
                    group
                });

                return from([
                    setCampaignAndGroupSelectorEntityType(entityToSelectType),
                    selectCampaignAndGroupSelectorCampaign(campaign),
                    selectCampaignAndGroupSelectorGroup(group),
                    canAddInDna ? acceptCampaignAndGroupSelectorDataDna() : acceptCampaignAndGroupSelectorData()
                ]);
            }
        )
    );
};

const goToCampaignsEditForCreationEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GO_TO_CAMPAIGNS_EDIT_FOR_CREATION),
        switchMap(
            (action: GoToCampaignsEditForCreationAction): Observable<Action> => {
                const { login, campaignType } = action;

                CampaignsIdsForCampaignsEditHelpers.setCampaignsIds(login, []);

                return of(
                    changeLocation(
                        UrlHelpers.getCampaignsEditUrl({
                            [CampaignsScreenUrlSearchQueryParams.ulogin]: login,
                            [CampaignsScreenUrlSearchQueryParams.isNew]: '1',
                            [CampaignsScreenUrlSearchQueryParams.campaignType]: campaignType
                        }),
                        undefined,
                        false
                    )
                );
            }
        )
    );
};

const goToBannersEditForCreationEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GO_TO_BANNERS_EDIT_FOR_CREATION),
        mergeMap(
            (action: GoToBannersEditForCreationAction): Observable<PushAction | ResetGridData> => {
                const { login, campaignId, groupId } = action;
                const groupsIds: Array<GroupId> = [groupId];
                const campaignsIds = [campaignId];
                const { fromOldInterface } = store$.value.commonData;

                BannersIdsForBannersEditHelpers.setCampAndGroupIdsForCreation(login, campaignsIds, groupsIds);

                return from([
                    changeLocation(
                        UrlHelpers.getBannersEditUrl({
                            [BannersScreenUrlSearchQueryParams.ulogin]: login,
                            [BannersScreenUrlSearchQueryParams.campaignsIds]: campaignsIds,
                            [BannersScreenUrlSearchQueryParams.groupsIds]: groupsIds,
                            [BannersScreenUrlSearchQueryParams.isNew]: '1',
                            from_old_interface: fromOldInterface ? '1' : undefined
                        }),
                        undefined,
                        false
                    ),
                    resetGridData()
                ]);
            }
        )
    );
};

const gridScreenGoToLogsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.ofType(GRID_SCREEN_GO_TO_LOGS).map(() => {
        const state = store$.value;
        const sourceParams = selectGridSourceParams(state);
        const { selectionInfo } = state.grid;

        if (sourceParams.login) {
            return changeLocation(UrlHelpers.getLogsUrl(), {
                cid: selectionInfo.campaigns.join(', '),
                login: sourceParams.login
            });
        } else {
            throw new Error(`Не указан логин клиента`);
        }
    });
};

const gridScreenHandleNavigationLinkClickEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$
        .ofType(GRID_SCREEN_HANDLE_NAVIGATION_LINK_CLICK)
        .map((action: GridScreenHandleNavigationLinkClickAction) => {
            const state = store$.value;
            const { selectionInfo } = state.grid;
            const sourceParams = selectGridSourceParams(state);

            const { view } = sourceParams;

            const actionsDictionary = {
                campaigns: {
                    [CampaignsAction.goToStatistics]: () => goToStatistics(action.target),
                    [CampaignsAction.goToLogs]: () => goToLogs(),
                    [CampaignsAction.goToCampaignModerationOffer]: () => goToCampaignModerationOffer(),
                    [CampaignsAction.payByCash]: () => goToDirectPaymentInstructions(),
                    [CampaignsAction.goToCampaignModerationOffer]: () => goToCampaignModerationOffer(),
                    [CampaignsAction.payByCash]: () => goToDirectPaymentInstructions(),
                    [CampaignsAction.exportToExcelWithBanners]: () => exportToExcel(XlsExportMode.withBanners),
                    [CampaignsAction.exportToExcelWithoutBanners]: () => exportToExcel(XlsExportMode.withoutBanners)
                },
                groups: {
                    [GroupsAction.goToStatistics]: () => goToStatistics(action.target),
                    [GroupsAction.editGroups]: () =>
                        goToGroupsEdit(state.commonData.login, selectionInfo.campaigns, selectionInfo.groups),
                    [GroupsAction.exportToExcelWithBanners]: () => exportToExcel(XlsExportMode.withBanners),
                    [GroupsAction.exportToExcelWithoutBanners]: () => exportToExcel(XlsExportMode.withoutBanners)
                },
                banners: {
                    [BannersAction.editBanners]: () => goToBannersMassEdit(),
                    [BannersAction.editBannersInternal]: () => goToInternalBannersMassEdit(),
                    [BannersAction.exportToExcel]: () => exportToExcel(XlsExportMode.withBanners)
                },
                phrases: {},
                targeting: {}
            };

            if (!actionsDictionary[view] || !actionsDictionary[view][action.linkAction]) {
                throw new Error('Unsupported navigation link');
            }

            return actionsDictionary[view][action.linkAction]();
        });
};

const gridScreenHandleColumnsVisibilityChangeEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$
        .ofType(GRID_SCREEN_HANDLE_COLUMNS_VISIBILITY_CHANGE)
        .map((action: GridScreenHandleColumnsVisibilityChangeAction) => {
            const { columnsSettings } = store$.value.grid;
            const state = store$.value;
            const sourceParams = selectGridSourceParams(state);

            const settings = getColumnsVisibilitySettings(columnsSettings, action.columns, sourceParams.view);

            LocalStorage.setItem(StorageKeyType.columnsSettings, settings);

            return gridScreenChangeColumnsVisibility(settings);
        });
};

const gridScreenHandleColumnsOrderChangeEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$
        .ofType(GRID_SCREEN_HANDLE_COLUMNS_ORDER_CHANGE)
        .map((action: GridScreenHandleColumnsOrderChangeAction) => {
            const state = store$.value;
            const sourceParams = selectGridSourceParams(state);
            const { columnsSettings } = store$.value.grid;

            const settings = getColumnSettingsWithUpdatedOrder(columnsSettings, action.columnOrder, sourceParams.view);

            LocalStorage.setItem(StorageKeyType.columnsSettings, settings);

            return gridScreenChangeColumnsOrder(sourceParams.view, settings[sourceParams.view].columnOrder);
        });
};

const gridScreenHandleColumnsWidthUpdateEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(GRID_SCREEN_HANDLE_COLUMNS_WIDTH_UPDATE),
        mergeMap((action: GridScreenHandleColumnsWidthUpdateAction) => {
            const state = store$.value;
            const sourceParams = selectGridSourceParams(state);
            const { columnsSettings, isCompactViewEnabled } = store$.value.grid;

            if (action.key) {
                const settings = getColumnsWidthSettings(
                    columnsSettings,
                    action.key,
                    action.width,
                    sourceParams.view,
                    isCompactViewEnabled
                );

                LocalStorage.setItem(StorageKeyType.columnsSettings, settings);

                return of(gridScreenUpdateColumnsWidth(settings));
            }
            return EMPTY;
        })
    );
};

const gridScreenHandleItemActionEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.ofType(GRID_SCREEN_HANDLE_ITEM_ACTION).mergeMap(
        (action: GridScreenHandleItemAction): Observable<Action> => {
            const state = store$.value;
            const sourceParams = selectGridSourceParams(state);
            const { gridData, selectionInfo } = store$.value.grid;
            const { view: sourceParamsView, login } = sourceParams!;
            const groupsCacheKey = gridData.cacheKey;

            let coreActionObservable: Observable<Action>;

            if (sourceParamsView === groups) {
                const group = gridData.data.find(el => el && el.id === action.id);

                if (!group) {
                    throw new Error(`Группа с номером ${action.id} не найдена`);
                }

                if (action.action === GroupsAction.copy) {
                    return Observable.of(
                        openDuplicateGroupsModal(selectionInfo.campaigns, [action.id], groupsCacheKey || '', login)
                    );
                }
                coreActionObservable = handleGroupItemAction(
                    deps,
                    action.action as GroupsAction,
                    action.id,
                    (group as Group).name,
                    login
                );
            } else if (sourceParamsView === retargetings) {
                coreActionObservable = handleRetargetingsAction(
                    deps,
                    action.action as RetargetingsAction,
                    [action.id],
                    login
                );
            } else {
                throw new Error('Нет обработчика для массового действия');
            }

            coreActionObservable = coreActionObservable
                .catch(error => {
                    if (error instanceof NotSyncedError) {
                        return EMPTY;
                    }

                    if (error) {
                        const message =
                            error instanceof ApiMethodExecutingError
                                ? error.getMessage()
                                : error.message || getGridActionErrorMessage(action.action, false);

                        showNoticeMessage(NoticeType.error, message);
                    }
                    return EMPTY;
                })
                .mergeMap(
                    (finalAction): Observable<Action> => {
                        return Observable.concat(
                            Observable.of(resetGridData()),
                            Observable.of(finalAction),
                            Observable.of(updateActionProgress(false))
                        );
                    }
                );

            return Observable.concat(Observable.of(updateActionProgress(true)), coreActionObservable);
        }
    );
};

type HandleGroupActionObservable = UpdateGridSelection | OpenMinusPhraseSelectorGridModal;

const handleGroupItemAction = (
    deps: EpicDeps,
    action: GroupsAction,
    id: string,
    name: string,
    login: string
): Observable<HandleGroupActionObservable> => {
    switch (action) {
        case GroupsAction.remoderateBannersAdditions:
            return Observable.fromPromise(
                deps.groupsMutationExecutor.sendRemoderateAdsCallouts(
                    {
                        adGroupIds: [id]
                    },
                    login
                )
            ).mergeMap((result: RemoderateAdsCallouts) => {
                const {
                    remoderateAdsCallouts: { processedAdGroupIds }
                } = result;

                let noticeType: NoticeType = NoticeType.success;

                if (processedAdGroupIds.length === 0) {
                    noticeType = NoticeType.error;
                }

                const message = getGroupItemActionsResultMessage(action, noticeType, name);

                showNoticeMessage(noticeType, message);

                return EMPTY;
            });
        case GroupsAction.sendModerateByAgency:
        case GroupsAction.sendModerateByClient:
        case GroupsAction.admSendMD:
            return Observable.fromPromise(
                deps.restApiManager.executeDnaGroupsGridItemsAction(action, [id], login)
            ).mergeMap(response => {
                const paramsNoticeMassAction = getNoticeParamsForMassAction(response, 1);

                const message = getGroupItemActionsResultMessage(action, paramsNoticeMassAction.noticeType, name);

                showNoticeMessage(paramsNoticeMassAction.noticeType, message);

                return EMPTY;
            });
        case GroupsAction.moderateAcceptBannersAdditions:
            return Observable.fromPromise(confirm({ message: getTextForItemActionConfirm(action) })).mergeMap(() =>
                Observable.fromPromise(
                    deps.groupsMutationExecutor.sendRemoderateAcceptAdsCallouts(
                        {
                            adGroupIds: [id]
                        },
                        login
                    )
                ).mergeMap((result: RemoderateAdsCallouts) => {
                    const {
                        remoderateAdsCallouts: { processedAdGroupIds }
                    } = result;

                    let noticeType: NoticeType = NoticeType.success;

                    if (processedAdGroupIds.length === 0) {
                        noticeType = NoticeType.error;
                    }

                    const message = getGroupItemActionsResultMessage(action, noticeType, name);

                    showNoticeMessage(noticeType, message);

                    return EMPTY;
                })
            );
        case GroupsAction.moderateAccept:
        case GroupsAction.delete:
        case GroupsAction.sendBK:
            return Observable.fromPromise(
                confirm({
                    message: getTextForRowItemActionConfirm(action, name)
                })
            ).mergeMap(() =>
                Observable.fromPromise(deps.restApiManager.executeDnaGroupsGridItemsAction(action, [id], login)).map(
                    response => {
                        const paramsNoticeMassAction = getNoticeParamsForMassAction(response, 1);

                        const message = getGroupItemActionsResultMessage(
                            action,
                            paramsNoticeMassAction.noticeType,
                            name
                        );

                        showNoticeMessage(paramsNoticeMassAction.noticeType, message);

                        return updateSelection([]);
                    }
                )
            );
        default:
            throw new Error('Нет обработчика для массового действия над группами');
    }
};

const handleRetargetingsAction = (
    deps: EpicDeps,
    action: RetargetingsAction,
    retargetingIds: string[],
    login: string
): Observable<never> => {
    switch (action) {
        case RetargetingsAction.resume:
            return from(deps.retargetingMutationExecutor.resumeRetargetings(retargetingIds, login)).pipe(
                mergeMap(data => {
                    const { successCount, totalCount } = data.resumeRetargetings;

                    showRetargetingActionNoticeMessage(action, successCount, totalCount);
                    return EMPTY;
                })
            );
        case RetargetingsAction.suspend:
            return from(deps.retargetingMutationExecutor.suspendRetargetings(retargetingIds, login)).pipe(
                mergeMap(data => {
                    const { successCount, totalCount } = data.suspendRetargetings;

                    showRetargetingActionNoticeMessage(action, successCount, totalCount);
                    return EMPTY;
                })
            );
        case RetargetingsAction.delete:
            return from(
                confirm({
                    message: getTextForItemActionConfirm(RetargetingsAction.delete)
                })
            ).pipe(
                mergeMap(() =>
                    from(deps.retargetingMutationExecutor.deleteRetargetings(retargetingIds, login)).pipe(
                        mergeMap(data => {
                            const { successCount, totalCount } = data.deleteRetargetings;

                            showRetargetingActionNoticeMessage(action, successCount, totalCount);
                            return EMPTY;
                        })
                    )
                )
            );
        default:
            throw new Error('Нет обработчика для массового действия над условиями ретаргетинга');
    }
};

const exportToExcelEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) =>
    action$.pipe(
        ofType(EXPORT_TO_EXCEL),
        mergeMap(
            (action: ExportToExcelAction): Observable<Action> => {
                const store = store$.value;
                const ulogin = store.commonData.login;
                const route = store.router.params && store.router.params.view;
                const selectionInfo = store.grid.selectionInfo;

                const params: XlsExportParams = {};

                switch (route) {
                    case GridRouterParams.campaigns:
                        params.campaignIds = selectionInfo.campaigns.map(id => Number(id));
                        break;
                    case GridRouterParams.groups:
                        params.adGroupIds = selectionInfo.groups.map(id => Number(id));
                        break;
                    case GridRouterParams.banners:
                        params.adIds = selectionInfo.banners.map(id => Number(id));
                        break;
                    default:
                        throw new Error('Неизвестный тип импорта');
                }

                return from(deps.xlsDataProvider.exportXls(action.exportType, params, ulogin)).pipe(
                    mergeMap(
                        (res): Observable<Action> => {
                            if (res.success) {
                                of(
                                    alert({
                                        title: i18n('export-to-excel'),
                                        buttonText: i18n('close'),
                                        children: React.createElement(StyledLink, {
                                            href: res.result.exportFileUrl,
                                            target: LinkTarget.blank,
                                            children: i18n('download'),
                                            theme: StyledLinkTheme.blue
                                        })
                                    })
                                );
                            } else {
                                showNoticeMessage(NoticeType.error, i18n('error-message'));
                            }

                            return EMPTY;
                        }
                    ),
                    catchError(
                        (err): Observable<Action> => {
                            showNoticeMessage(NoticeType.error, i18n('error-message'));

                            return EMPTY;
                        }
                    )
                );
            }
        )
    );

export const gridScreenHelpersEpic = combineEpics(
    gridScreenCancelCellEditEpic,
    gridScreenGoToStatisticsEpic,
    gridScreenGoToCampaingsEditEpic,
    gridScreenGoToCampaignModerationOfferEpic,
    gridScreenGoToBannersEditEpic,
    gridScreenGoToBannersMassEditEpic,
    gridScreenGoToGroupsEditEpic,
    gridScreenGoToLogsEpic,
    gridScreenHandleNavigationLinkClickEpic,
    gridScreenHandleColumnsVisibilityChangeEpic,
    gridScreenHandleColumnsOrderChangeEpic,
    gridScreenHandleColumnsWidthUpdateEpic,
    goToGroupsEditForCreationEpic,
    goToGroupsEditForCreationFromGridEpic,
    goToBannerEditForCreationFromGridEpic,
    goToBannersEditForCreationEpic,
    goToInternalBannersEditForCreationEpic,
    goToInternalBannersEditEpic,
    goToInternalBannersMassEditEpic,
    gridScreenGoToDirectPaymentInstructions,
    gridScreenHandleItemActionEpic,
    goToCampaignsEditForCreationEpic,
    exportToExcelEpic
);
